import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, ShieldAlert } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ErrorState, Loading } from "@/components/ds";
import { PartnerHome } from "@/components/partner/PartnerHome";
import { PartnerStock } from "@/components/partner/PartnerStock";
import { PartnerReplenish } from "@/components/partner/PartnerReplenish";
import { PartnerReturns } from "@/components/partner/PartnerReturns";
import { PartnerClosures } from "@/components/partner/PartnerClosures";
import { PartnerHistory } from "@/components/partner/PartnerHistory";
import { NoticeBell } from "@/components/partner/NoticeBell";

const TABS = [
  ["painel", "Painel"],
  ["estoque", "Meu estoque"],
  ["repor", "Repor"],
  ["devolucoes", "Devoluções"],
  ["fechamentos", "Fechamentos"],
  ["historico", "Histórico"],
];

export default function PartnerArea() {
  const { logout, impersonating, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("painel");
  // Cada recarga da área também revalida os avisos (o sino ouve esta chave).
  const [reloads, setReloads] = useState(0);

  const load = useCallback(async () => {
    setError("");
    try {
      const [me, stock, catalog, reps, rets, moves, closures, preview] = await Promise.all([
        api.get("/partner/panel"),
        api.get("/partner/catalog"),
        api.get("/partner/replenishment-catalog"),
        api.get("/partner/replenishments"),
        api.get("/partner/returns"),
        api.get("/partner/movements"),
        api.get("/partner/closures"),
        api.get("/partner/closures/preview"),
      ]);
      setData({
        me: me.data,
        stock: stock.data,
        catalog: catalog.data,
        reps: reps.data,
        rets: rets.data,
        moves: moves.data,
        closures: closures.data,
        preview: preview.data,
      });
      setReloads((n) => n + 1);
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Abrindo sua área de parceiro..." />;

  return (
    <div className="min-h-screen bg-background" data-testid="partner-area">
      {impersonating && (
        <div
          className="sticky top-0 z-40 flex flex-wrap items-center justify-between gap-2 border-b border-amber-500/40 bg-amber-500/15 px-5 py-2.5 md:px-8"
          data-testid="impersonation-banner"
        >
          <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-200">
            <ShieldAlert className="h-4 w-4" /> Visualização administrativa — {impersonating}
          </p>
          <Button
            size="sm"
            variant="outline"
            onClick={stopImpersonation}
            data-testid="exit-impersonation-btn"
            className="h-8 gap-1.5 rounded-full border-amber-500/40 text-[11px]"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Voltar para Administração
          </Button>
        </div>
      )}
      <header className="sticky top-0 z-30 border-b border-white/10 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 py-4 md:px-8">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary font-display text-sm font-bold text-primary-foreground">
              T3
            </span>
            <div className="min-w-0">
              <p
                className="truncate font-display text-base font-semibold leading-none"
                data-testid="partner-header-name"
              >
                {data.me.name}
              </p>
              <p className="mt-1 text-[11px] text-muted-foreground">Área do parceiro · TR3ND</p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <NoticeBell onGoTo={setTab} refreshKey={reloads} />
            <button
              onClick={onLogout}
              data-testid="partner-logout-btn"
              className="shrink-0 rounded-full border border-white/10 p-2 text-muted-foreground transition-colors hover:text-destructive"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 pb-16 pt-6 md:px-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex h-auto w-full flex-wrap gap-1 bg-secondary/60">
            {TABS.map(([value, label]) => (
              <TabsTrigger key={value} value={value} data-testid={`partner-tab-${value}`}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="painel" className="mt-6">
            <PartnerHome me={data.me} onReload={load} onGoTo={setTab} />
          </TabsContent>
          <TabsContent value="estoque" className="mt-6">
            <PartnerStock stock={data.stock} movements={data.moves} onReload={load} />
          </TabsContent>
          <TabsContent value="repor" className="mt-6">
            <PartnerReplenish catalog={data.catalog} requests={data.reps}
                              lock={data.me?.replenish_lock} onReload={load} />
          </TabsContent>
          <TabsContent value="devolucoes" className="mt-6">
            <PartnerReturns stock={data.stock} returns={data.rets} onReload={load} />
          </TabsContent>
          <TabsContent value="fechamentos" className="mt-6">
            <PartnerClosures preview={data.preview} closures={data.closures} onReload={load} />
          </TabsContent>
          <TabsContent value="historico" className="mt-6">
            <PartnerHistory movements={data.moves} closures={data.closures} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
